import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { WebPageSchema, BreadcrumbListSchema } from '@/components/feature/SchemaOrg';
import { categories } from '@/mocks/siteData';

const categoryImages: Record<string, string> = {
  sports: 'https://readdy.ai/api/search-image?query=Dynamic%20dark%20abstract%20sports%20illustration%20with%20golden%20energy%20lines%2C%20stadium%20atmosphere%20concept%2C%20elegant%20geometric%20patterns%20with%20amber%20accents%2C%20competitive%20sports%20theme%2C%20cinematic%20lighting%20on%20dark%20background%2C%20professional%20sports%20betting%20aesthetic&width=1200&height=600&seq=cat-sports-01&orientation=landscape',
  live: 'https://readdy.ai/api/search-image?query=Elegant%20dark%20abstract%20live%20casino%20illustration%20with%20golden%20card%20patterns%2C%20luxurious%20gaming%20table%20atmosphere%2C%20warm%20amber%20lighting%20effects%2C%20premium%20casino%20theme%20on%20dark%20background%2C%20cinematic%20professional%20composition&width=1200&height=600&seq=cat-live-01&orientation=landscape',
  slots: 'https://readdy.ai/api/search-image?query=Colorful%20dark%20abstract%20slot%20machine%20illustration%20with%20golden%20reels%20and%20symbols%2C%20elegant%20geometric%20patterns%2C%20warm%20amber%20accents%20on%20dark%20background%2C%20premium%20gaming%20theme%2C%20cinematic%20lighting%2C%20luxurious%20casino%20aesthetic&width=1200&height=600&seq=cat-slots-01&orientation=landscape',
  lottery: 'https://readdy.ai/api/search-image?query=Mysterious%20dark%20abstract%20lottery%20illustration%20with%20golden%20number%20balls%2C%20elegant%20floating%20patterns%2C%20warm%20amber%20highlights%20on%20dark%20background%2C%20premium%20lottery%20game%20theme%2C%20cinematic%20atmospheric%20lighting&width=1200&height=600&seq=cat-lottery-01&orientation=landscape',
  esports: 'https://readdy.ai/api/search-image?query=Futuristic%20dark%20abstract%20esports%20illustration%20with%20golden%20circuit%20patterns%2C%20gaming%20arena%20atmosphere%2C%20elegant%20geometric%20design%2C%20warm%20amber%20accents%20on%20dark%20background%2C%20competitive%20gaming%20theme%2C%20cinematic%20lighting&width=1200&height=600&seq=cat-esports-01&orientation=landscape',
  fishing: 'https://readdy.ai/api/search-image?query=Dreamy%20dark%20abstract%20ocean%20fishing%20illustration%20with%20golden%20underwater%20patterns%2C%20elegant%20marine%20geometric%20design%2C%20warm%20amber%20accents%20on%20dark%20background%2C%20premium%20fishing%20game%20theme%2C%20cinematic%20atmospheric%20lighting&width=1200&height=600&seq=cat-fishing-01&orientation=landscape',
};

export default function CategoriesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || categories[0].id;
  const [activeTab, setActiveTab] = useState(initialTab);
  const currentCategory = categories.find((c) => c.id === activeTab) || categories[0];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <ScrollToTop />
      <WebPageSchema
        title="产品分类 - 开云官网"
        description="开云平台提供丰富的产品服务分类，涵盖体育竞技、真人视讯、电子游艺、彩票游戏、电子竞技、捕鱼游戏等多元品类。"
        url="https://www.kaiyun.com/categories"
      />
      <BreadcrumbListSchema
        items={[
          { name: '首页', url: 'https://www.kaiyun.com/' },
          { name: '产品分类', url: 'https://www.kaiyun.com/categories' },
        ]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16">
          <Breadcrumb items={[{ label: '产品分类' }]} />

          <main>
            <section className="relative py-16 md:py-20 bg-background-100">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <div className="text-center mb-10">
                  <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                    丰富的<strong className="text-primary-500">产品服务</strong>
                  </h1>
                  <p className="text-secondary-600 max-w-2xl mx-auto">
                    开云平台提供多元化的产品服务，满足不同用户的娱乐需求
                  </p>
                </div>

                <div className="flex flex-wrap justify-center gap-2 mb-12">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleTabChange(cat.id)}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        activeTab === cat.id
                          ? 'bg-primary-500 text-background-50 shadow-lg shadow-primary-500/20'
                          : 'bg-background-50 text-secondary-600 hover:text-foreground-900 hover:bg-background-200/50 border border-background-200/50'
                      }`}
                    >
                      <i className={`${cat.icon} w-4 h-4 flex items-center justify-center`}></i>
                      {cat.name}
                    </button>
                  ))}
                </div>

                <div className="rounded-2xl bg-background-50 border border-background-200/50 overflow-hidden">
                  <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
                    <div className="lg:col-span-2 relative h-64 lg:h-auto">
                      <img
                        src={categoryImages[currentCategory.id]}
                        alt={currentCategory.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="lg:col-span-3 p-8 md:p-10 flex flex-col justify-center">
                      <div className="w-14 h-14 rounded-xl bg-primary-500/10 flex items-center justify-center mb-5">
                        <i className={`${currentCategory.icon} text-2xl text-primary-500 w-7 h-7 flex items-center justify-center`}></i>
                      </div>
                      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
                        {currentCategory.name}
                      </h2>
                      <p className="text-secondary-600 leading-relaxed mb-6">
                        {currentCategory.description}
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        {currentCategory.features.map((feature) => (
                          <div key={feature} className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-primary-500 w-4 h-4 flex items-center justify-center"></i>
                            <span className="text-sm text-secondary-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </main>
        </div>
        <Footer />
      </div>
    </>
  );
}